import { createTheme } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { componentColors, cutCorners } from '../styles/themes/themeExtensions'

// Both palettes follow the Teleo brand: a stark chassis, structural grays, one
// accent. Dark is near-black with elevated panels; light is Ghost White with
// white panels. Custom slots (accentTint, chart, panel) are read by components
// through theme.palette so no colour is hard-coded elsewhere.
const FONT_FAMILY = "'Archivo', Arial, sans-serif"

const darkPalette = {
  mode: 'dark',
  primary: {
    main: '#0ddfcb', // accent, lit for dark surfaces
    dark: '#0aa89d',
    contrastText: '#000000',
  },
  secondary: {
    main: '#013933', // Pine Green
  },
  neutral: {
    main: '#788990', // Gunmetal Gray
  },
  background: {
    default: '#07090a', // chassis
    paper: '#121a1d', // elevated panel
    panel2: '#172124', // nested elevation, row hover
  },
  text: {
    primary: '#f3f5fc',
    secondary: 'rgba(255, 255, 255, 0.7)',
    disabled: 'rgba(255, 255, 255, 0.4)',
  },
  divider: 'rgba(255, 255, 255, 0.12)',
  action: {
    hover: 'rgba(255, 255, 255, 0.06)',
    selected: 'rgba(13, 223, 203, 0.12)',
    focus: 'rgba(13, 223, 203, 0.24)',
  },
  accentTint: {
    subtle: 'rgba(13, 223, 203, 0.12)',
    strong: 'rgba(13, 223, 203, 0.24)',
  },
  chart: {
    series: '#0ddfcb',
    comparison: '#788990',
    areaFill: '#0ddfcb',
    barTop: '#06c0af',
    barBottom: '#088779',
  },
  error: {
    main: '#ef4e5c',
  },
  warning: {
    main: '#ed6c02',
  },
  success: {
    main: '#0ddfcb', // "up" reads in the accent, per the brand
  },
}

const lightPalette = {
  mode: 'light',
  primary: {
    main: '#038880', // Cadet Blue
    dark: '#026e68',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#013933', // Pine Green
  },
  neutral: {
    main: '#788990', // Gunmetal Gray
  },
  background: {
    default: '#f3f5fc', // Ghost White
    paper: '#ffffff',
    panel2: '#f3f5fc',
  },
  text: {
    primary: '#000000',
    secondary: '#788990', // Gunmetal Gray
    disabled: '#a9b2b9', // Steel Silver
  },
  divider: '#a9b2b9', // Steel Silver hairline
  action: {
    hover: 'rgba(3, 136, 128, 0.12)',
    selected: 'rgba(3, 136, 128, 0.12)',
    focus: 'rgba(3, 136, 128, 0.24)',
  },
  accentTint: {
    subtle: 'rgba(3, 136, 128, 0.12)',
    strong: 'rgba(3, 136, 128, 0.24)',
  },
  chart: {
    series: '#038880',
    comparison: '#788990',
    areaFill: '#038880',
    barTop: '#038880',
    barBottom: '#026e68',
  },
  error: {
    main: '#d32f2f',
  },
  warning: {
    main: '#ed6c02',
  },
  success: {
    main: '#038880',
  },
}

// Expanded, heavy, uppercase: titles, stat numerals, the vehicle name
const displayType = {
  fontFamily: FONT_FAMILY,
  fontStretch: '125%',
  fontWeight: 800,
  letterSpacing: '-0.01em',
  textTransform: 'uppercase',
}

// Create theme function
export const createAppTheme = (mode) => {
  const palette = mode === 'dark' ? darkPalette : lightPalette

  return createTheme({
    palette: {
      ...palette,
      status: componentColors.status,
      video: componentColors.video,
    },
    shape: { borderRadius: 0 },
    typography: {
      fontFamily: FONT_FAMILY,
      h1: displayType,
      h2: displayType,
      h3: displayType,
      h4: displayType,
      h5: { ...displayType, fontSize: '1.25rem' },
      h6: { fontWeight: 700, letterSpacing: '0.02em' },
      // Uppercase, letter-spaced metadata: section eyebrows, axis labels, table headers
      overline: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        lineHeight: 1.4,
      },
      button: { fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            backgroundColor: palette.background.default,
            fontVariantNumeric: 'tabular-nums',
            '&::-webkit-scrollbar': {
              width: '8px',
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: palette.divider,
              '&:hover': {
                background: palette.text.disabled,
              },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none', borderRadius: 0 },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { ...cutCorners(8), boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { ...cutCorners(6), fontWeight: 600, letterSpacing: '0.04em' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 700,
            fontSize: '0.8125rem',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 2, borderRadius: 0 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 0 },
          notchedOutline: { borderColor: palette.divider },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 0,
            backgroundColor: mode === 'dark' ? palette.background.paper : '#000000',
            border: `1px solid ${palette.divider}`,
            color: mode === 'dark' ? palette.text.primary : '#ffffff',
          },
          arrow: { color: mode === 'dark' ? palette.background.paper : '#000000' },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: 0, border: `1px solid ${palette.divider}`, boxShadow: 'none' },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 0 },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: { borderRadius: 0 },
        },
      },
    },
  })
}

const ThemeContext = createContext({
  mode: 'dark',
  toggleMode: () => {},
  theme: null,
})

export const useThemeMode = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeProvider')
  }
  return context
}

export const ThemeProvider = ({ children }) => {
  // Get initial mode from localStorage or default to 'dark'
  const [mode, setMode] = useState(() => {
    try {
      const savedMode = localStorage.getItem('themeMode')
      return savedMode === 'light' ? 'light' : 'dark'
    } catch {
      // Handle localStorage errors (quota exceeded, disabled, etc.)
      // Default to dark mode if localStorage is unavailable
      return 'dark'
    }
  })

  // Create theme based on mode
  const theme = useMemo(() => createAppTheme(mode), [mode])

  // Toggle between dark and light mode
  const toggleMode = () => {
    const newMode = mode === 'dark' ? 'light' : 'dark'
    setMode(newMode)
    try {
      localStorage.setItem('themeMode', newMode)
    } catch (error) {
      // Handle localStorage errors gracefully (quota exceeded, disabled, etc.)
      // Theme state is still updated in memory, just not persisted
      if (error.name === 'QuotaExceededError' || error.code === 22) {
        // Try to clear other localStorage entries if quota exceeded
        try {
          // Clear old geocoding cache if available
          const keys = Object.keys(localStorage)
          const geocodingKeys = keys.filter((key) => key.startsWith('geocoding_'))
          geocodingKeys.slice(0, Math.floor(geocodingKeys.length / 2)).forEach((key) => {
            localStorage.removeItem(key)
          })
          // Retry setting theme mode
          localStorage.setItem('themeMode', newMode)
        } catch {
          // If retry fails, theme mode is still updated in memory
        }
      }
    }
  }

  // Update document body class for CSS customizations
  useEffect(() => {
    document.body.classList.remove('light-mode', 'dark-mode')
    document.body.classList.add(`${mode}-mode`)
  }, [mode])

  const value = {
    mode,
    toggleMode,
    theme,
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
